import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { get, post } from "../../../components/Utils/CallApi";
import { setPageLoadingTrue, setPageLoadingFalse } from "../appSlice";
import { setError } from "../appSlice";

export const fetchSlideshow = createAsyncThunk(
  "slideshow/fetchSlideshow",
  async (companyId, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setPageLoadingTrue());
      const params = "type=Slideshow&companyId=" + companyId;
      const response = await get("content/get", params);

      if (response.status === "failure") {
        dispatch(setError(response.result));
        return rejectWithValue(response.result);
      }

      return response.result || [];
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

export const saveSlideshow = createAsyncThunk(
  "slideshow/saveSlideshow",
  async (
    { allSlideshowData, sectionId },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const payload = {
        type: "Slideshow",
        data: allSlideshowData.data,
      };

      dispatch(setPageLoadingTrue());
      const state = getState();
      const response = await post(
        "content/save",
        payload,
        state.app,
        sectionId
      );

      if (response.status === "failure") {
        dispatch(setError(response.result));
        return rejectWithValue(response.result);
      }

      return response.data;
    } catch (error) {
      dispatch(setError("An error has occurred"));
      return rejectWithValue(error.message);
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

export const getSlideItem = createAsyncThunk(
  "slideshow/getSlideItem",
  async ({ id }, { dispatch, getState, rejectWithValue }) => {
    try {
      dispatch(setPageLoadingTrue());
      const state = getState();

      const params = `type=Slideshow&companyId=${state.app.companyId}`;
      const response = await get("content/get", params);

      if (response.status === "failure") {
        dispatch(setError(response.result));
        return rejectWithValue(response.result);
      }

      // For slideshow, just return the first item from any language
      const firstItem = response.result?.[0];
      if (firstItem) {
        const contentData = JSON.parse(firstItem.content || "{}");
        return {
          data: {
            id: 1,
            name: contentData.name || "",
            url: contentData.url || "",
            buttonLabel: contentData.buttonLabel || "",
            desktopImage: firstItem.desktopImage || "",
            mobileImage: firstItem.mobileImage || "",
          },
        };
      }

      return {
        data: {
          id: 1,
          name: "",
          url: "",
          buttonLabel: "",
          desktopImage: "",
          mobileImage: "",
        },
      };
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

export const deleteSlideItem = createAsyncThunk(
  "slideshow/deleteSlideItem",
  async ({ id, sectionId }, { dispatch, getState, rejectWithValue }) => {
    try {
      dispatch(setPageLoadingTrue());
      const state = getState();

      // For content-based slideshow, we'll clear the content
      // Structure empty data by language codes
      const emptyData = {
        en: { name: "", url: "", buttonLabel: "" },
        fr: { name: "", url: "", buttonLabel: "" },
        ar: { name: "", url: "", buttonLabel: "" },
      };

      const payload = {
        companyId: state.app.companyId,
        type: "Slideshow",
        data: emptyData,
      };

      const response = await post(
        "content/save",
        payload,
        state.app,
        sectionId
      );

      if (response.status === "failure") {
        dispatch(setError(response.result));
        return rejectWithValue(response.result);
      }

      return id;
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

export const orderSlideshowItems = createAsyncThunk(
  "slideshow/orderSlideshowItems",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setPageLoadingTrue());
      const response = await post("sections/data/order", data);

      if (response.status === "failure") {
        dispatch(setError(response.result));
        return rejectWithValue(response.result);
      }

      return response.data;
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

const slideshowSlice = createSlice({
  name: "slideshow",
  initialState: {
    slideshowItems: [],
    currentItem: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearSlideshowError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch slideshow
      .addCase(fetchSlideshow.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSlideshow.fulfilled, (state, action) => {
        state.loading = false;
        state.slideshowItems = action.payload;
      })
      .addCase(fetchSlideshow.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Save slideshow
      .addCase(saveSlideshow.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveSlideshow.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(saveSlideshow.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get slide item
      .addCase(getSlideItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSlideItem.fulfilled, (state, action) => {
        state.loading = false;
        state.currentItem = action.payload;
      })
      .addCase(getSlideItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete slide item
      .addCase(deleteSlideItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSlideItem.fulfilled, (state, action) => {
        state.loading = false;
        state.slideshowItems = state.slideshowItems.filter(
          (item) => item.id !== action.payload
        );
      })
      .addCase(deleteSlideItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Order slideshow items
      .addCase(orderSlideshowItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(orderSlideshowItems.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(orderSlideshowItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearSlideshowError } = slideshowSlice.actions;

export default slideshowSlice.reducer;
